import { inject, Injectable, NgZone } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { Subject, Subscription, take } from 'rxjs';
import { KeycloakService } from 'keycloak-angular';
import { ConnectionStatusService } from './connection-status.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { Router } from '@angular/router';
import { DialogService } from './dialog.service';
import { PromptDialogComponent } from '../modules/shared/components/_dialog/prompt-dialog/prompt-dialog.component';
import { getCounterTime, getSecondsToExpiration } from '../utils/session.utils';
import { SECONDS_BEFORE_SESSION_ALERT } from '../config/general.config';
import { SyncProgressService } from '../modules/go/services/sync-progress.service';

@Injectable({
  providedIn: 'root',
})
export class SessionService {
  readonly _ngZone = inject(NgZone);
  readonly _keycloakService = inject(KeycloakService);
  readonly _connectionStatusService = inject(ConnectionStatusService);
  readonly _loader = inject(NgxSpinnerService);
  readonly _router = inject(Router);
  readonly _dialogService = inject(DialogService);
  readonly _syncProgressService = inject(SyncProgressService);

  _sessionTimeout$ = new Subject<string>();
  public sessionTimeout$ = this._sessionTimeout$.asObservable();

  private _sessionTimeout = 0;
  private _sessionIntervalHandler: ReturnType<typeof setInterval> | null = null;
  private _sessionExpirationDialogRef: MatDialogRef<PromptDialogComponent> | null = null;
  private _expirationPromptOpened = false;
  private _logoutInProgress = false;
  private _networkStatusSubscription: Subscription | null = null;
  private _syncStateSubscription: Subscription | null = null;
  private _refreshPromise: Promise<boolean> | null = null;
  private _syncInProgress = false;
  private _syncDialogOpen = false;
  private _lastSyncKeepAliveAttemptMs = 0;
  private _sessionRefreshInProgress = false;
  private _terminalSessionError = false;
  private readonly refreshRetryScheduleMs = [0, 5000, 10000, 20000];
  private readonly minSyncKeepAliveIntervalMs = 15000;
  private readonly maxSyncKeepAliveIntervalMs = 60000;
  private _syncKeepAliveIntervalMs = 30000;

  private get _keycloak() {
    return this._keycloakService.getKeycloakInstance();
  }

  public startSession(): void {
    if (this._syncStateSubscription === null) {
      this._syncStateSubscription = this._syncProgressService.state$.subscribe((state) => {
        this._syncInProgress = state.status === 'running';
        if ((this._syncInProgress || this._syncDialogOpen) && this._expirationPromptOpened) {
          this._sessionExpirationDialogRef?.close();
          this._sessionExpirationDialogRef = null;
          this._expirationPromptOpened = false;
        }
      });
    }

    if (this._networkStatusSubscription === null) {
      this._networkStatusSubscription = this._connectionStatusService.networkStatus$.subscribe((isOnline) => {
        if (!isOnline) {
          this.stopSessionMonitoring();
          return;
        }

        if (this._logoutInProgress || !this._keycloak.authenticated || this._sessionIntervalHandler !== null) {
          return;
        }

        this.syncSessionState();
        this.monitorSession();
      });
    }
  }

  private syncSessionState(): void {
    const refreshTokenExpiration = this._keycloak.refreshTokenParsed?.exp;
    this._sessionTimeout = refreshTokenExpiration ? refreshTokenExpiration * 1000 : 0;
    this.recalculateSyncKeepAliveInterval();
  }

  private syncSessionStateIfExtended(): void {
    const refreshTokenExpiration = this._keycloak.refreshTokenParsed?.exp;
    const nextSessionTimeout = refreshTokenExpiration ? refreshTokenExpiration * 1000 : 0;

    if (nextSessionTimeout > this._sessionTimeout) {
      this._sessionTimeout = nextSessionTimeout;
      this.recalculateSyncKeepAliveInterval();
    }
  }

  private monitorSession(): void {
    this._sessionIntervalHandler = this._ngZone.runOutsideAngular(() =>
      setInterval(() => {
        this.syncSessionStateIfExtended();
        const secondsToExpiration = getSecondsToExpiration(this._sessionTimeout);
        this._ngZone.run(() => this._sessionTimeout$.next(getCounterTime(secondsToExpiration)));
        this._ngZone.run(() => this.updateExpirationDialogCountdown(secondsToExpiration));

        if (this._syncInProgress || this._syncDialogOpen) {
          void this._ngZone.run(() => this.keepSessionAliveDuringSync(secondsToExpiration));
          return;
        }

        if (
          secondsToExpiration <= SECONDS_BEFORE_SESSION_ALERT &&
          secondsToExpiration > 0 &&
          !this._expirationPromptOpened &&
          !this._sessionRefreshInProgress &&
          !this._terminalSessionError
        ) {
          void this._ngZone.run(() => this.openExpirationDialog());
        }

        if (secondsToExpiration <= 0) {
          void this._ngZone.run(() => this.handleSessionExpired());
        }
      }, 1000)
    );
  }

  setSyncDialogOpen(isOpen: boolean): void {
    this._syncDialogOpen = isOpen;
    if (isOpen && this._expirationPromptOpened) {
      this._sessionExpirationDialogRef?.close();
      this._sessionExpirationDialogRef = null;
      this._expirationPromptOpened = false;
    }
  }

  isSyncDialogOpen(): boolean {
    return this._syncDialogOpen;
  }

  async refreshSession(minValidity = -1): Promise<boolean> {
    if (this._logoutInProgress || !this._connectionStatusService.isOnline()) {
      return false;
    }

    if (this._refreshPromise !== null) {
      return this._refreshPromise;
    }

    this._refreshPromise = this.performRefresh(minValidity).finally(() => {
      this._refreshPromise = null;
    });

    return this._refreshPromise;
  }

  async refreshSessionForSync(): Promise<boolean> {
    // Force refresh call while sync dialog is open to keep session alive deterministically.
    return this.refreshSession(-1);
  }

  logout(): void {
    if (this._logoutInProgress) {
      return;
    }

    this._logoutInProgress = true;
    this.stopSessionMonitoring();
    void this._loader.show();

    if (!this._connectionStatusService.isOnline()) {
      void this._router.navigate(['/']);
      return;
    }

    void this._router.navigate(['/'])
    .then(() => this._keycloak.createLoginUrl())
    .then((loginUrl) => this._keycloak.logout({ redirectUri: loginUrl }));
  }

  private openExpirationDialog(): void {
    if (this._sessionRefreshInProgress || this._terminalSessionError) {
      return;
    }

    this._expirationPromptOpened = true;
    this._sessionExpirationDialogRef = this._dialogService.promptForSessionExpiration();
    this.updateExpirationDialogCountdown(getSecondsToExpiration(this._sessionTimeout));

    this._sessionExpirationDialogRef.afterClosed().pipe(take(1)).subscribe({
      next: async (result) => {
        if (!result) {
          this._sessionExpirationDialogRef = null;
          this._expirationPromptOpened = false;
          const isOnline = await new Promise<boolean>((resolve) => {
            this._connectionStatusService.networkStatus$.pipe(take(1)).subscribe(resolve);
          });
          if (isOnline) {
            this.logout();
          }
          return;
        }

        this._sessionRefreshInProgress = true;
        let refreshed = false;
        try {
          refreshed = await this.refreshSession();
        } catch {
          refreshed = false;
        }
        this._sessionRefreshInProgress = false;
        this._sessionExpirationDialogRef = null;

        if (!refreshed) {
          this._expirationPromptOpened = false;
          void this.handleSessionExpiredOnline();
          return;
        }

        this._expirationPromptOpened = false;
      },
    });
  }

  private handleSessionExpired(): void {
    if (this._logoutInProgress) {
      return;
    }

    this._connectionStatusService.networkStatus$.pipe(take(1)).subscribe((isOnline) => {
      if (isOnline) {
        this.logout();
        return;
      }

      this.stopSessionMonitoring();
      void this._loader.show();
      void this._router.navigate(['/']);
    });
  }

  private async handleSessionExpiredOnline(): Promise<void> {
    if (this._logoutInProgress) {
      return;
    }

    const isOnline = await new Promise<boolean>((resolve) => {
      this._connectionStatusService.networkStatus$.pipe(take(1)).subscribe(resolve);
    });

    if (isOnline) {
      this.logout();
      return;
    }

    this.stopSessionMonitoring();
    void this._loader.show();
    await this._router.navigate(['/']);
  }

  private stopSessionMonitoring(): void {
    if (this._sessionIntervalHandler !== null) {
      clearInterval(this._sessionIntervalHandler);
      this._sessionIntervalHandler = null;
    }

    this._sessionTimeout$.next('00:00');
    this._sessionExpirationDialogRef?.close();
    this._sessionExpirationDialogRef = null;
    this._expirationPromptOpened = false;
    this._sessionRefreshInProgress = false;
  }

  private updateExpirationDialogCountdown(secondsToExpiration: number): void {
    const dialogRef = this._sessionExpirationDialogRef;
    const promptData = dialogRef?.componentInstance?.promptData;

    if (!dialogRef || !promptData) {
      return;
    }

    promptData.headerParam = getCounterTime(Math.max(secondsToExpiration, 0));
    dialogRef.componentRef?.changeDetectorRef.detectChanges();
  }

  private async performRefresh(minValidity: number): Promise<boolean> {
    for (const delayMs of this.refreshRetryScheduleMs) {
      if (this._logoutInProgress || !this._connectionStatusService.isOnline()) {
        return false;
      }

      if (delayMs > 0) {
        await this.wait(delayMs);
      }

      try {
        await this._keycloak.updateToken(minValidity);
        this.syncSessionState();
        this._terminalSessionError = false;
        return true;
      } catch (error) {
        if (this.isTerminalRefreshError(error) || !this.isRetryableRefreshError(error)) {
          if (this.shouldDeferTerminalRefreshFailure()) {
            return false;
          }
          this._terminalSessionError = true;
          this.logout();
          return false;
        }
      }
    }

    return false;
  }

  private async keepSessionAliveDuringSync(secondsToExpiration: number): Promise<void> {
    if (secondsToExpiration <= 0) {
      await this.handleSessionExpiredOnline();
      return;
    }

    const now = Date.now();
    if (now - this._lastSyncKeepAliveAttemptMs < this._syncKeepAliveIntervalMs) {
      return;
    }

    this._lastSyncKeepAliveAttemptMs = now;
    await this.refreshSessionForSync();
  }

  private recalculateSyncKeepAliveInterval(): void {
    const sessionDurationSeconds = this.getSessionDurationSeconds();
    const derivedIntervalMs = Math.floor((sessionDurationSeconds * 1000) / 3);
    this._syncKeepAliveIntervalMs = Math.max(
      this.minSyncKeepAliveIntervalMs,
      Math.min(this.maxSyncKeepAliveIntervalMs, derivedIntervalMs)
    );
  }

  private getSessionDurationSeconds(): number {
    const token = this._keycloak.refreshTokenParsed;
    const exp = Number(token?.exp ?? 0);
    const iat = Number(token?.iat ?? 0);
    if (exp > iat && iat > 0) {
      return exp - iat;
    }

    const remaining = getSecondsToExpiration(this._sessionTimeout);
    return Math.max(remaining, SECONDS_BEFORE_SESSION_ALERT);
  }

  private getSyncRefreshMinValiditySeconds(): number {
    const sessionDurationSeconds = this.getSessionDurationSeconds();
    const remainingSeconds = getSecondsToExpiration(this._sessionTimeout);
    const safeRemaining = Math.max(remainingSeconds, 30);
    const derived = Math.floor(Math.min(sessionDurationSeconds, safeRemaining) * 0.5);
    return Math.max(30, Math.min(SECONDS_BEFORE_SESSION_ALERT, derived));
  }

  private isTerminalRefreshError(error: any): boolean {
    const status = Number(error?.response?.status ?? error?.status ?? 0);
    if (status === 400 || status === 401 || status === 403) {
      return true;
    }

    const nestedErrorCode = String(
      error?.error
      ?? error?.errorCode
      ?? error?.response?.data?.error
      ?? error?.response?.error
      ?? ''
    ).toLowerCase();
    if (nestedErrorCode === 'invalid_grant') {
      return true;
    }

    const description = String(
      error?.error_description
      ?? error?.response?.data?.error_description
      ?? error?.response?.error_description
      ?? error?.message
      ?? ''
    ).toLowerCase();

    if (
      description.includes('invalid_grant')
      || description.includes('session not active')
      || description.includes('token is not active')
      || description.includes('refresh token')
    ) {
      return true;
    }

    let snapshot = '';
    try {
      snapshot = JSON.stringify(error ?? {}).toLowerCase();
    } catch {
      snapshot = String(error ?? '').toLowerCase();
    }
    return snapshot.includes('invalid_grant')
      || snapshot.includes('session not active')
      || snapshot.includes('token is not active');
  }

  private isRetryableRefreshError(error: any): boolean {
    const status = Number(error?.response?.status ?? error?.status ?? 0);
    if (status >= 500) {
      return true;
    }

    const description = String(
      error?.error_description
      ?? error?.response?.data?.error_description
      ?? error?.response?.error_description
      ?? error?.message
      ?? ''
    ).toLowerCase();

    if (
      description.includes('timeout')
      || description.includes('network')
      || description.includes('failed to fetch')
      || description.includes('temporarily unavailable')
    ) {
      return true;
    }
    return false;
  }

  private shouldDeferTerminalRefreshFailure(): boolean {
    return (this._syncInProgress || this._syncDialogOpen) && getSecondsToExpiration(this._sessionTimeout) > 0;
  }

  private wait(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}
